import { useCallback, useMemo } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GalleryPhoto {
  image?: string;
  original_url?: string;
  alt_text?: string;
  title?: string;
}

interface POI {
  poi_name?: string;
  poi_type?: string;
  poi_featured_image?: string;
}

// ─── Slot Group Definitions ───────────────────────────────────────────────────

const neighborhoodImageDef: SlotGroupDefinition = {
  label: 'Neighborhood Section Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[4].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Neighborhood',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select the photo_gallery image that best evokes the surrounding neighborhood or local lifestyle — an exterior, view, courtyard, or outdoor/community scene rather than a unit interior.',
    context:
      'Image paired with the home page "In Our Neighborhood" section, alongside an accordion of nearby points of interest; favor something that reflects the property’s setting and surroundings.',
    sourceCollection: 'photo_gallery',
  },
};

// ─── Component ─────────────────────────────────────────────────────────────────

export function NeighborhoodHomeSection() {
  const neighborhoodHeading = useContentToken('neighborhoodhomesection.heading');

  const neighborhoodImage = useSlotGroup('neighborhoodhomesection.image', neighborhoodImageDef);

  const galleryImages = useCollectionBinding<GalleryPhoto>(
    'neighborhoodhomesection.gallery_images',
    {
      collection: 'photo_gallery',
      defaultLimit: 12,
    },
  );
  const pois = useCollectionBinding<POI>('neighborhoodhomesection.neighborhood_pois', {
    collection: 'neighborhood_pois',
    defaultLimit: 40,
  });

  const resolveTileImage = useCallback(
    (slotUrl: string | null | undefined, index: number) => {
      const fallback = galleryImages[index] ?? null;
      return slotUrl || fallback?.image || fallback?.original_url || '';
    },
    [galleryImages],
  );
  const resolveTileAlt = useCallback(
    (slotAlt: string | null | undefined, index: number, defaultAlt: string) => {
      const fallback = galleryImages[index] ?? null;
      return slotAlt || fallback?.alt_text || defaultAlt;
    },
    [galleryImages],
  );

  const neighborhoodImageUrl = resolveTileImage(neighborhoodImage.image, 4);
  const neighborhoodImageAlt = resolveTileAlt(neighborhoodImage.alt, 4, 'Neighborhood');

  const poiGroups = useMemo(() => {
    const groups: Record<string, string[]> = {};
    for (const poi of pois) {
      const type = poi.poi_type?.trim() || 'Other';
      if (!groups[type]) groups[type] = [];
      if (poi.poi_name) groups[type].push(poi.poi_name);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [pois]);

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[42%_1fr] gap-10 lg:gap-16 items-start">
          {/* Left: heading + accordion */}
          <div>
            <EditableToken
              path="neighborhoodhomesection.heading"
              defaultValue="In Our Neighborhood"
              as="h2"
              className="font-heading text-3xl sm:text-4xl font-normal text-[var(--vibe-color-text)] mb-4 leading-tight"
            >
              {neighborhoodHeading}
            </EditableToken>

            {poiGroups.length > 0 && (
              <Accordion.Root
                type="single"
                collapsible
                defaultValue={poiGroups[0][0]}
                className="divide-y divide-[var(--vibe-color-border)]"
              >
                {poiGroups.map(([category, names]) => (
                  <Accordion.Item key={category} value={category}>
                    <Accordion.Header>
                      <Accordion.Trigger className="overview-poi-trigger w-full flex items-center justify-between py-4 text-left group">
                        <span className="font-heading text-base font-semibold text-[var(--vibe-color-text)] group-hover:text-[var(--vibe-color-primary)] transition-colors">
                          {category}
                        </span>
                        <span className="overview-poi-icon text-[var(--vibe-color-text-muted)] text-xl leading-none flex-shrink-0 ml-4 select-none transition-transform duration-200">
                          +
                        </span>
                      </Accordion.Trigger>
                    </Accordion.Header>
                    <Accordion.Content className="overview-poi-content overflow-hidden">
                      <ul className="pb-4 space-y-2.5">
                        {names.map((name, i) => (
                          <li
                            key={i}
                            className="font-body text-sm text-[var(--vibe-color-text-muted)]"
                          >
                            {name}
                          </li>
                        ))}
                      </ul>
                    </Accordion.Content>
                  </Accordion.Item>
                ))}
              </Accordion.Root>
            )}
          </div>

          {/* Right: photo */}
          <div className="lg:sticky lg:top-24">
            <EditableSlotGroup
              groupPath="neighborhoodhomesection.image"
              definition={neighborhoodImageDef}
              className="block"
            >
              {neighborhoodImageUrl ? (
                <VibesitesImage
                  url={neighborhoodImageUrl}
                  alt={neighborhoodImageAlt}
                  className="w-full rounded-xl object-cover"
                  style={{ aspectRatio: '4/3' }}
                />
              ) : (
                <div
                  className="w-full rounded-xl bg-[var(--vibe-color-surface)]"
                  style={{ aspectRatio: '4/3' }}
                />
              )}
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .overview-poi-trigger[data-state='open'] .overview-poi-icon {
          transform: rotate(45deg);
        }
        .overview-poi-content[data-state='open'] {
          animation: overview-poi-down 250ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .overview-poi-content[data-state='closed'] {
          animation: overview-poi-up 200ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes overview-poi-down {
          from { height: 0; opacity: 0; }
          to { height: var(--radix-accordion-content-height); opacity: 1; }
        }
        @keyframes overview-poi-up {
          from { height: var(--radix-accordion-content-height); opacity: 1; }
          to { height: 0; opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .overview-poi-content[data-state='open'],
          .overview-poi-content[data-state='closed'] {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodHomeSection',
  displayName: 'Neighborhood Home Section',
  tokens: {
    'neighborhoodhomesection.heading': {
      label: 'Neighborhood Heading',
      type: 'text',
      defaultValue: 'In Our Neighborhood',
    },
  },
  slotGroups: {
    'neighborhoodhomesection.image': {
      label: 'Neighborhood Section Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'photo_gallery[4].image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Neighborhood',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 4,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
      generator: {
        prompt:
          'Select the photo_gallery image that best evokes the surrounding neighborhood or local lifestyle — an exterior, view, courtyard, or outdoor/community scene rather than a unit interior.',
        context:
          'Image paired with the home page "In Our Neighborhood" section, alongside an accordion of nearby points of interest; favor something that reflects the property’s setting and surroundings.',
        sourceCollection: 'photo_gallery',
      },
    },
  },
  collectionBindings: {
    'neighborhoodhomesection.gallery_images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      defaultLimit: 12,
    },
    'neighborhoodhomesection.neighborhood_pois': {
      label: 'Neighborhood Points of Interest',
      collection: 'neighborhood_pois',
      defaultLimit: 40,
    },
  },
};

(NeighborhoodHomeSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NeighborhoodHomeSection;
